import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// Helper to get the correct path based on user type
const getBasePath = () => {
  const userType = localStorage.getItem("userType");
  return userType === 'brand' ? '/brand' : '';
};
import { LogOut, Folder, FilePenLine, Plus, ChevronRight, ChevronDown, MoreHorizontal, Edit2, Trash2, FolderPlus, FolderOpen } from "lucide-react";
import { API_ROOT, Project, projectAPI, Analysis, analysisAPI } from "@/config";
import ProjectModal from "./ui/project-modal";
import AnalysisModal from "./ui/analysis-modal";
import SidebarProfile from "./ui/sidebar-profile";
import LoadingSpinner from "./ui/loading-spinner";

interface AnalysisSidebarProps {
  onHideSidebar?: () => void;
}

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const AnalysisSidebar = ({ onHideSidebar }: AnalysisSidebarProps) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [projectAnalyses, setProjectAnalyses] = useState<Record<number, Analysis[]>>({});
  const [unassignedAnalyses, setUnassignedAnalyses] = useState<Analysis[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Modal states
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showProjectMenu, setShowProjectMenu] = useState<number | null>(null);
  
  // Analysis modal state
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [analysisModalMode, setAnalysisModalMode] = useState<'rename' | 'move' | 'delete'>('rename');
  const [editingAnalysis, setEditingAnalysis] = useState<Analysis | null>(null);
  const [showAnalysisMenu, setShowAnalysisMenu] = useState<string | null>(null);
  
  const navigate = useNavigate();

  // Fetch projects and analyses
  const fetchData = useCallback(async (loadMore = false) => {
    const pageToFetch = loadMore ? currentPage + 1 : 1;
    console.log(`🔄 CACHE-FIXED: Starting fetchData v2.0 - Page ${pageToFetch} ${loadMore ? '(Load More)' : ''}`);
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      if (loadMore) {
        setIsLoadingMore(true);
      }

      // Initialize with safe defaults (no projects in simplified backend)
      let finalAnalysesData = [];

      try {
        // Fetch analyses only (projects removed in backend cleanup)
        const analysesResponse = await fetch(`${API_ROOT}/analyses?page=${pageToFetch}&limit=100`, {
          headers: { "Authorization": `Bearer ${token}` }
        });

        if (analysesResponse.status === 401) {
          localStorage.removeItem("token");
          navigate("/");
          return;
        }

        if (!analysesResponse.ok) {
          console.error("🚨 CACHE-FIXED: HTTP error - analyses:", analysesResponse.status);
          throw new Error("Failed to fetch data");
        }

        // Parse analyses with bulletproof handling
        try {
          const rawAnalysesData = await analysesResponse.json();
          console.log("✅ CACHE-FIXED: Raw analyses response:", rawAnalysesData);
          console.log("✅ CACHE-FIXED: Response type:", typeof rawAnalysesData);
          console.log("✅ CACHE-FIXED: Response keys:", Object.keys(rawAnalysesData));
          
          // Super safe analysis data extraction with pagination support
          let paginationData = null;
          
          if (Array.isArray(rawAnalysesData)) {
            finalAnalysesData = rawAnalysesData;
            console.log("✅ CACHE-FIXED: Direct array format -", finalAnalysesData.length, "items");
          } else if (rawAnalysesData && typeof rawAnalysesData === 'object' && Array.isArray(rawAnalysesData.analyses)) {
            finalAnalysesData = rawAnalysesData.analyses;
            paginationData = rawAnalysesData.pagination;
            console.log("✅ CACHE-FIXED: Paginated format -", finalAnalysesData.length, "items, page", paginationData?.page || 1);
            
            // Update pagination state
            if (paginationData) {
              setCurrentPage(paginationData.page || 1);
              setHasMorePages(paginationData.has_next || false);
            }
          } else {
            console.warn("🚨 CACHE-FIXED: Unexpected response format, using empty array");
            finalAnalysesData = [];
          }
        } catch (error) {
          console.error("🚨 CACHE-FIXED: Error parsing analyses:", error);
          finalAnalysesData = [];
        }

      } catch (fetchError) {
        console.error("🚨 CACHE-FIXED: Fetch error:", fetchError);
        finalAnalysesData = [];
      }

      // Triple-check array is actually an array
      if (!Array.isArray(finalAnalysesData)) {
        console.error("🚨 CACHE-FIXED: Analyses not array, forcing empty:", typeof finalAnalysesData);
        finalAnalysesData = [];
      }

      console.log("✅ CACHE-FIXED: Final safe data - Analyses:", finalAnalysesData.length);

      // Set state with guaranteed arrays (no projects in simplified backend)
      setProjects([]); // Always empty since projects removed
      
      if (loadMore) {
        // Append new analyses to existing ones
        setAnalyses(prev => [...prev, ...finalAnalysesData]);
        setUnassignedAnalyses(prev => [...prev, ...finalAnalysesData]); // Display in sidebar
        console.log("✅ CACHE-FIXED: Appended", finalAnalysesData.length, "more analyses");
      } else {
        // Replace analyses with fresh data
        setAnalyses(finalAnalysesData);
        setUnassignedAnalyses(finalAnalysesData); // Display in sidebar
        console.log("✅ CACHE-FIXED: Set", finalAnalysesData.length, "analyses");
      }

    } catch (error) {
      console.error("🚨 CACHE-FIXED: Final error catch:", error);
      if (!loadMore) {
        // Only clear state on fresh load errors, not on load more errors
        setAnalyses([]);
      }
    } finally {
      console.log("✅ CACHE-FIXED: Finished, setting loading to false");
      if (loadMore) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [navigate]);

  // Fetch data on mount and when component becomes visible
  useEffect(() => {
    fetchData();
    
    // Also refresh when window gains focus (user returns to tab)
    const handleFocus = () => {
      console.log("🔄 SIDEBAR: Window focused, refreshing data");
      fetchData();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for sidebar refresh requests (after new analysis completion)
  useEffect(() => {
    const checkRefreshFlag = () => {
      const shouldRefresh = localStorage.getItem("refreshSidebar");
      const refreshTimestamp = localStorage.getItem("refreshSidebarTimestamp");
      
      if (shouldRefresh === "true" && refreshTimestamp) {
        const timestamp = parseInt(refreshTimestamp);
        const now = Date.now();
        
        // Only refresh if the flag is recent (within last 5 minutes)
        if (now - timestamp < 300000) {
          console.log("🔄 SIDEBAR REFRESH: Detected new analysis, refreshing sidebar data");
          localStorage.removeItem("refreshSidebar");
          localStorage.removeItem("refreshSidebarTimestamp");
          
          // Reset pagination and force refresh
          setCurrentPage(1);
          setHasMorePages(false);
          fetchData();
        } else {
          // Clean up old flags
          localStorage.removeItem("refreshSidebar");
          localStorage.removeItem("refreshSidebarTimestamp");
        }
      }
    };

    // Check immediately and then every 2 seconds
    checkRefreshFlag();
    const interval = setInterval(checkRefreshFlag, 2000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMoreAnalyses = useCallback(async () => {
    if (!isLoadingMore && hasMorePages) {
      console.log("📄 LOAD MORE: Loading page", currentPage + 1);
      await fetchData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingMore, hasMorePages, currentPage]);

  const handleNewAnalysis = () => {
    navigate(`${getBasePath()}/upload`);
  };

  const handleCreateProject = () => {
    setModalMode('create');
    setEditingProject(null);
    setIsProjectModalOpen(true);
  };

  const handleEditProject = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setModalMode('edit');
    setEditingProject(project);
    setIsProjectModalOpen(true);
    setShowProjectMenu(null);
  };

  const handleDeleteProject = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowProjectMenu(null);
    
    if (window.confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone.`)) {
      try {
        console.log("🔄 Sidebar: Deleting project and refreshing data");
        setIsLoading(true);
        await projectAPI.delete(project.id);
        await fetchData(); // Refresh data
      } catch (error) {
        console.error("Error deleting project:", error);
        alert("Failed to delete project. Please try again.");
        setIsLoading(false);
      }
    }
  };

  const handleProjectSuccess = async () => {
    console.log("🔄 Sidebar: Project operation completed, refreshing data");
    setIsLoading(true);
    await fetchData(); // Refresh data after creating/editing project
  };

  const toggleProject = (projectId: number) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  const handleProjectClick = (projectId: number, e: React.MouseEvent) => {
    // Check if the click was on the menu button or chevron
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('.chevron-icon')) {
      return; // Don't navigate if clicking on menu or chevron
    }
    
    // Navigate to video uploader with project pre-selected
    navigate(`/upload?project_id=${projectId}`);
  };

  const handleProjectMenuToggle = (projectId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowProjectMenu(showProjectMenu === projectId ? null : projectId);
  };

  const handleAnalysisMenuToggle = (analysisId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAnalysisMenu(showAnalysisMenu === analysisId ? null : analysisId);
  };

  const handleRenameAnalysis = (analysis: Analysis, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAnalysis(analysis);
    setAnalysisModalMode('rename');
    setIsAnalysisModalOpen(true);
    setShowAnalysisMenu(null);
  };

  const handleMoveAnalysis = (analysis: Analysis, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAnalysis(analysis);
    setAnalysisModalMode('move');
    setIsAnalysisModalOpen(true);
    setShowAnalysisMenu(null);
  };

  const handleDeleteAnalysis = (analysis: Analysis, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAnalysis(analysis);
    setAnalysisModalMode('delete');
    setIsAnalysisModalOpen(true);
    setShowAnalysisMenu(null);
  };

  const handleAnalysisSuccess = (updatedAnalysis: Analysis) => {
    console.log("🔄 Sidebar: Analysis operation completed, refreshing data");
    if (analysisModalMode === 'delete') {
      // Remove from state
      setAnalyses(prev => prev.filter(a => a.unique_id !== updatedAnalysis.unique_id));
      setProjectAnalyses(prev => {
        const newMap: Record<number, Analysis[]> = {};
        Object.entries(prev).forEach(([projectId, analyses]) => {
          newMap[parseInt(projectId)] = analyses.filter(a => a.unique_id !== updatedAnalysis.unique_id);
        });
        return newMap;
      });
      setUnassignedAnalyses(prev => prev.filter(a => a.unique_id !== updatedAnalysis.unique_id));
    } else {
      // Update in state and refresh to ensure proper organization
      setAnalyses(prev => prev.map(a => a.unique_id === updatedAnalysis.unique_id ? updatedAnalysis : a));
      setIsLoading(true);
      fetchData();
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowProjectMenu(null);
      setShowAnalysisMenu(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <>
      <aside 
        style={{ 
          background: PALETTE.brightSnow,
          color: PALETTE.gunmetal,
          borderRight: `1px solid ${PALETTE.americanSilver}`
        }} 
        className="h-screen w-64 flex flex-col"
      >
        {/* Fixed Top Section */}
        <div className="flex-shrink-0">
          {/* Header with logo and hide button */}
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: PALETTE.americanSilver }}>
            <div className="flex items-center gap-3">
              <img 
                src="/brandcomply_logo.png" 
                alt="brandcomply logo" 
                className="h-6 w-6"
              />
            </div>

            {onHideSidebar && (
              <button
                onClick={onHideSidebar}
                style={{
                  background: 'transparent', 
                  border: 'none', 
                  color: PALETTE.gunmetal, 
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px'
                }}
                className="hover:bg-gray-100 transition-all hide-sidebar-button"
                title="Hide sidebar"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={PALETTE.gunmetal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="18" x="3" y="3" rx="2"/>
                  <path d="M9 3v18"/>
                </svg>
              </button>
            )}
          </div>

          {/* New Analysis Button */}
          <div
            onClick={handleNewAnalysis}
            className="flex items-center gap-3 mx-4 my-3 px-3 py-2 rounded-lg cursor-pointer transition-all hover:opacity-80"
            style={{ 
              background: PALETTE.inchworm,
              color: PALETTE.gunmetal
            }}
          >
            <FilePenLine className="w-4 h-4 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                New Check
              </div>
            </div>
          </div>

          {/* Create Project Button */}
          <div
            onClick={handleCreateProject}
            className="flex items-center gap-3 mx-4 mb-3 px-3 py-2 rounded-lg cursor-pointer transition-all hover:bg-gray-100 new-project-button"
          >
            <FolderPlus className="w-4 h-4 flex-shrink-0" style={{ color: PALETTE.gunmetal }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>
                New Project
              </div>
            </div>
          </div>

          <div style={{ backgroundColor: PALETTE.americanSilver, height: '1px', width: '100%' }}></div>
        </div>

        {/* Scrollable Projects Section */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <div className="space-y-1">
              {/* Loading state */}
              {isLoading && (
                <div className="py-8">
                  <LoadingSpinner size="md" text="Loading projects and analyses..." />
                </div>
              )}

              {/* Projects - only show when not loading */}
              {!isLoading && projects.map((project) => (
                <div key={project.id} className="space-y-1">
                  <div
                    className="flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-all hover:bg-gray-100 group"
                    onClick={(e) => handleProjectClick(project.id, e)}
                    title="Click to create new analysis in this project"
                  >
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                      <div 
                        className="chevron-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleProject(project.id);
                        }}
                      >
                        {expandedProjects.has(project.id) ? (
                          <ChevronDown className="w-3 h-3 flex-shrink-0" style={{ color: PALETTE.gunmetal }} />
                        ) : (
                          <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: PALETTE.gunmetal }} />
                        )}
                      </div>
                      <Folder className="w-4 h-4 flex-shrink-0" style={{ color: PALETTE.gunmetal }} />
                      <div className="text-sm truncate font-medium" style={{ color: PALETTE.gunmetal }}>
                        {project.name}
                      </div>
                    </div>
                    
                    {/* Project menu */}
                    <div className="relative">
                      <button
                        onClick={(e) => handleProjectMenuToggle(project.id, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-all"
                      >
                        <MoreHorizontal className="w-3 h-3" style={{ color: PALETTE.gunmetal }} />
                      </button>
                      
                      {showProjectMenu === project.id && (
                        <div 
                          className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-10 min-w-32"
                          style={{ borderColor: PALETTE.americanSilver }}
                        >
                          <button
                            onClick={(e) => handleEditProject(project, e)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100 rounded-t-lg"
                            style={{ color: PALETTE.gunmetal }}
                          >
                            <Edit2 className="w-3 h-3" />
                            Rename
                          </button>
                          <button
                            onClick={(e) => handleDeleteProject(project, e)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100 rounded-b-lg text-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Project analyses */}
                  {expandedProjects.has(project.id) && (
                    <div className="ml-6 space-y-1">
                      {projectAnalyses[project.id]?.length > 0 ? (
                        projectAnalyses[project.id].map((analysis) => (
                          <div
                            key={analysis.unique_id}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all hover:bg-gray-100 group"
                            onClick={() => navigate(`${getBasePath()}/result/${analysis.unique_id}`)}
                          >
                            <div className="w-4 h-4 flex-shrink-0"></div> {/* Spacer for alignment */}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm truncate" style={{ color: PALETTE.gunmetal }}>
                                {analysis.campaignName || "Untitled"}
                              </div>
                            </div>
                            <div className="relative">
                              <button
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
                                onClick={(e) => handleAnalysisMenuToggle(analysis.unique_id, e)}
                              >
                                <MoreHorizontal className="w-3 h-3" style={{ color: PALETTE.gunmetal }} />
                              </button>
                              {showAnalysisMenu === analysis.unique_id && (
                                <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[120px]">
                                  <button
                                    className="w-full px-3 py-1 text-left text-xs hover:bg-gray-100 flex items-center gap-2"
                                    onClick={(e) => handleRenameAnalysis(analysis, e)}
                                  >
                                    <Edit2 className="w-3 h-3" />
                                    Rename
                                  </button>
                                  <button
                                    className="w-full px-3 py-1 text-left text-xs hover:bg-gray-100 flex items-center gap-2"
                                    onClick={(e) => handleMoveAnalysis(analysis, e)}
                                  >
                                    <FolderOpen className="w-3 h-3" />
                                    Move
                                  </button>
                                  <button
                                    className="w-full px-3 py-1 text-left text-xs hover:bg-gray-100 flex items-center gap-2 text-red-600"
                                    onClick={(e) => handleDeleteAnalysis(analysis, e)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-xs opacity-60" style={{ color: PALETTE.gunmetal }}>
                          No analyses yet
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Unassigned analyses - only show when not loading */}
              {!isLoading && unassignedAnalyses.length > 0 && (
                <div className="space-y-1 mt-4">
                  <div className="px-2 py-1">
                    <div className="text-xs font-semibold opacity-60" style={{ color: PALETTE.gunmetal }}>
                      Compliance Checks
                    </div>
                  </div>
                  {unassignedAnalyses.map((analysis) => (
                    <div
                      key={analysis.unique_id}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all hover:bg-gray-100 group"
                      onClick={() => navigate(`${getBasePath()}/result/${analysis.unique_id}`)}
                    >
                      <FilePenLine className="w-4 h-4 flex-shrink-0" style={{ color: PALETTE.gunmetal }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate" style={{ color: PALETTE.gunmetal }}>
                          {analysis.campaignName || "Untitled"}
                        </div>
                      </div>
                      <div className="relative">
                        <button
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
                          onClick={(e) => handleAnalysisMenuToggle(analysis.unique_id, e)}
                        >
                          <MoreHorizontal className="w-3 h-3" style={{ color: PALETTE.gunmetal }} />
                        </button>
                        {showAnalysisMenu === analysis.unique_id && (
                          <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[120px]">
                            <button
                              className="w-full px-3 py-1 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                              onClick={(e) => handleRenameAnalysis(analysis, e)}
                            >
                              <Edit2 className="w-3 h-3" />
                              Rename
                            </button>
                            <button
                              className="w-full px-3 py-1 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                              onClick={(e) => handleMoveAnalysis(analysis, e)}
                            >
                              <FolderOpen className="w-3 h-3" />
                              Move
                            </button>
                            <button
                              className="w-full px-3 py-1 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-red-600"
                              onClick={(e) => handleDeleteAnalysis(analysis, e)}
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Load More Button */}
              {!isLoading && hasMorePages && (
                <div className="mx-4 my-3">
                  <button
                    onClick={loadMoreAnalyses}
                    disabled={isLoadingMore}
                    className="w-full px-3 py-2 text-sm rounded-lg border transition-all hover:bg-gray-50 disabled:opacity-50"
                    style={{ 
                      borderColor: PALETTE.americanSilver,
                      color: PALETTE.gunmetal 
                    }}
                  >
                    {isLoadingMore ? "Loading more..." : `Load More Analyses (${currentPage + 1}+)`}
                  </button>
                </div>
              )}

              {/* Empty state - only show when not loading and no data */}
              {!isLoading && projects.length === 0 && unassignedAnalyses.length === 0 && (
                <div className="text-center py-8" style={{ color: PALETTE.gunmetal }}>
                  <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No projects or analyses yet</p>
                  <p className="text-xs opacity-60 mt-1">Create a project to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Bottom Section - User Profile */}
        <div className="flex-shrink-0 border-t" style={{ borderColor: PALETTE.americanSilver }}>
          <SidebarProfile />
        </div>
      </aside>

      {/* Project Modal */}
      <ProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        onSuccess={handleProjectSuccess}
        project={editingProject}
        mode={modalMode}
      />

      {/* Analysis Modal */}
      <AnalysisModal
        isOpen={isAnalysisModalOpen}
        onClose={() => setIsAnalysisModalOpen(false)}
        onSuccess={handleAnalysisSuccess}
        analysis={editingAnalysis}
        mode={analysisModalMode}
      />
    </>
  );
};

export default AnalysisSidebar;